import axios, { AxiosResponse } from 'axios';
import {
  CreatePaymentMethodRequest,
  CreatePaymentMethodResponse,
  PaymentMethodListResponse,
  PaymentMethodDetailsResponse,
  DeletePaymentMethodResponse,
  PaymentMethodErrorResponse,
  AdminPaymentMethodListResponse,
  AdminPaymentMethodDetailsResponse,
  AdminDeletePaymentMethodResponse,
  AdminPaymentMethodErrorResponse,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const paymentMethodApi = {
  // Create Payment Method for a Payer (ProtectPay endpoint with Basic auth)
  createPaymentMethod: async (
    payerId: string,
    request: CreatePaymentMethodRequest,
    billerId: string,
    authToken: string
  ): Promise<CreatePaymentMethodResponse> => {
    const authHeader = `Basic ${btoa(`${billerId}:${authToken}`)}`;
    const response: AxiosResponse<CreatePaymentMethodResponse> = await apiClient.put(
      `/protectpay/Payers/${payerId}/PaymentMethods/`,
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },

  // List Payment Methods for a Payer (ProtectPay endpoint with Basic auth)
  listPaymentMethods: async (
    payerId: string,
    billerId: string,
    authToken: string,
    limit?: number,
    offset?: number
  ): Promise<PaymentMethodListResponse> => {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const queryString = params.toString();
    const url = `/protectpay/Payers/${payerId}/PaymentMethods/${queryString ? `?${queryString}` : ''}`;

    const authHeader = `Basic ${btoa(`${billerId}:${authToken}`)}`;
    const response: AxiosResponse<PaymentMethodListResponse> = await apiClient.get(url, {
      headers: {
        Authorization: authHeader,
      },
    });
    return response.data;
  },

  // List Payment Methods for a Payer (Admin endpoint - no auth required)
  adminListPaymentMethodsByPayerId: async (
    payerId: string,
    limit?: number,
    offset?: number
  ): Promise<AdminPaymentMethodListResponse> => {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());

    const queryString = params.toString();
    const url = `/admin/protectpay/payers/${payerId}/paymentmethods${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse<AdminPaymentMethodListResponse> = await apiClient.get(url);
    return response.data;
  },

  // Get Payment Method Details (Admin endpoint)
  adminGetPaymentMethodDetails: async (
    paymentMethodId: string
  ): Promise<AdminPaymentMethodDetailsResponse> => {
    const response: AxiosResponse<AdminPaymentMethodDetailsResponse> = await apiClient.get(
      `/admin/protectpay/paymentmethods/${paymentMethodId}`
    );
    return response.data;
  },

  // Delete Payment Method (Admin endpoint)
  adminDeletePaymentMethod: async (
    paymentMethodId: string
  ): Promise<AdminDeletePaymentMethodResponse> => {
    const response: AxiosResponse<AdminDeletePaymentMethodResponse> = await apiClient.delete(
      `/admin/protectpay/paymentmethods/${paymentMethodId}`
    );
    return response.data;
  },
};
